import { GeneratedGenieDocument, GenieDocumentBrand } from "@/types/genie-documents/genie-document";

interface DocumentPreviewProps {
  doc: GeneratedGenieDocument;
  brand: GenieDocumentBrand;
  /** When true, lays out at fixed A4 width with print margins for PDF rasterization. */
  forPrint?: boolean;
}

/** Eyebrow label shown above the title, per document type. */
const TEMPLATE_EYEBROW: Record<string, string> = {
  faq: "Frequently Asked Questions",
  "how-to": "Step-by-Step Guide",
  "one-pager": "Overview",
};

/** Inline formatter: turns **bold** into <strong> so raw asterisks never leak into the document. */
const renderInline = (text: string): (string | JSX.Element)[] => {
  const parts: (string | JSX.Element)[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let last = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <strong key={key++} className="font-semibold text-gray-900">
        {match[1]}
      </strong>,
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : [text];
};

/** Light plain-text renderer: blank lines separate paragraphs; "- "/"1. " become lists. */
const renderBody = (body: string) => {
  const lines = body.split(/\r?\n/);
  const blocks: JSX.Element[] = [];
  let list: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let key = 0;

  const flush = () => {
    if (list.length && listType) {
      const items = list.map((t, i) => (
        <li key={i} className="mb-1">
          {renderInline(t)}
        </li>
      ));
      blocks.push(
        listType === "ul" ? (
          <ul key={`b${key++}`} className="list-disc pl-5 mb-3 text-gray-700">
            {items}
          </ul>
        ) : (
          <ol key={`b${key++}`} className="list-decimal pl-5 mb-3 text-gray-700">
            {items}
          </ol>
        ),
      );
    }
    list = [];
    listType = null;
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    const bullet = line.match(/^[-*]\s+(.*)$/);
    const numbered = line.match(/^\d+[.)]\s+(.*)$/);
    if (bullet) {
      if (listType && listType !== "ul") flush();
      listType = "ul";
      list.push(bullet[1]);
    } else if (numbered) {
      if (listType && listType !== "ol") flush();
      listType = "ol";
      list.push(numbered[1]);
    } else {
      flush();
      blocks.push(
        <p key={`b${key++}`} className="mb-3 text-gray-700 leading-relaxed">
          {renderInline(line)}
        </p>,
      );
    }
  }
  flush();
  return blocks;
};

export const DocumentPreview = ({ doc, brand, forPrint = false }: DocumentPreviewProps) => {
  const primary = brand.primaryColor || "#4E9CFF";
  const secondary = brand.secondaryColor || "#FF4DB8";
  const initial = (brand.name || "G").trim().charAt(0).toUpperCase();
  const eyebrow = TEMPLATE_EYEBROW[doc.template] ?? "Guide";

  return (
    <div
      data-doc-print={forPrint ? "true" : undefined}
      className="bg-white text-gray-900"
      style={{
        width: forPrint ? 794 : "100%",
        padding: forPrint ? "52px 56px 40px" : 0,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {/* Co-branded masthead — customer brand LEADS (left), Help Genie present (right) */}
      <div className="flex items-start justify-between gap-4 pb-6">
        <div className="flex items-center gap-3.5 min-w-0">
          {brand.logoUrl ? (
            <img
              src={brand.logoUrl}
              alt=""
              crossOrigin="anonymous"
              className="h-14 w-14 rounded-xl object-cover shrink-0"
            />
          ) : (
            <div
              className="h-14 w-14 rounded-xl flex items-center justify-center text-white text-2xl font-bold shrink-0"
              style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}
            >
              {initial}
            </div>
          )}
          <div className="min-w-0">
            <p className="text-lg font-bold text-gray-900 leading-tight truncate">{brand.name}</p>
            {brand.tagline ? (
              <p className="text-xs text-gray-500 mt-0.5 truncate">{brand.tagline}</p>
            ) : null}
          </div>
        </div>

        {/* Help Genie co-brand mark */}
        <div className="flex flex-col items-end gap-1 shrink-0 pt-0.5">
          <span className="text-[10px] uppercase tracking-[0.15em] text-gray-400">Made with</span>
          <img src="/helpgenie.png" alt="Help Genie" className="h-[18px] w-auto" />
        </div>
      </div>

      {/* Signature gradient bar — customer brand colors */}
      <div
        className="h-1.5 w-full rounded-full"
        style={{ background: `linear-gradient(90deg, ${primary}, ${secondary})` }}
      />

      {/* Title block */}
      <div className="mt-8 mb-9">
        <p
          className="text-[11px] font-semibold uppercase tracking-[0.18em] mb-2.5"
          style={{ color: primary }}
        >
          {eyebrow}
        </p>
        <h1 className="text-[32px] leading-[1.15] font-bold text-gray-900">{doc.title}</h1>
        {doc.subtitle ? <p className="text-base text-gray-500 mt-2.5">{doc.subtitle}</p> : null}
      </div>

      {/* Sections — numbered for a structured, designed layout */}
      <div className="space-y-7">
        {doc.sections.map((s, i) => (
          <section key={i} style={{ breakInside: "avoid" }} className="flex gap-4">
            <div
              className="shrink-0 h-8 w-8 rounded-lg flex items-center justify-center text-sm font-bold text-white mt-0.5"
              style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}
            >
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-[17px] font-semibold text-gray-900 mb-1.5 leading-snug">{s.heading}</h2>
              <div className="text-[15px]">{renderBody(s.body)}</div>
            </div>
          </section>
        ))}
      </div>

      {/* Co-brand footer — customer name (left) + prominent Help Genie logo (right) */}
      <div
        className="mt-12 pt-5 border-t flex items-center justify-between gap-4"
        style={{ borderColor: "#E5EAF0" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="h-2 w-2 rounded-full shrink-0"
            style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}
          />
          <span className="text-xs text-gray-400 truncate">{brand.name}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] text-gray-400">Powered by</span>
          <img src="/helpgenie.png" alt="Help Genie" className="h-4 w-auto" />
        </div>
      </div>
    </div>
  );
};
